import { memo, useMemo } from 'react';
import { useDispatch, useSelector } from '@bonanza-social/toolkit/state';
import { useNavigation, StackNavigationProp } from '@bonanza-social/navigation';
import { selectWelcomePromos } from '@bonanza-social/promos/state';
import {
  BonanzaLogo,
  Box,
  Button,
  Skeleton,
  useSafeAreaInsets,
} from '@bonanza-social/moonlight';
import { selectAccount } from '@bonanza-social/account';
import {
  getButtonState,
  handleVerifyAccount,
  handleVerifyIdentity,
} from './OnboardingHeader.utils';
import { ButtonState } from './OnboardingHeader.types';
import useOnfido from '../../hooks/useOnfido';
import BonusInfo from '../BonusInfo/BonusInfo';

function OnboardingHeader() {
  const { top } = useSafeAreaInsets();
  const dispatch = useDispatch();
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const onfido = useOnfido();
  const promos = useSelector(selectWelcomePromos);
  const account = useSelector(selectAccount);
  const buttonState = useMemo(() => getButtonState(account), [account]);
  const actions = useMemo(
    () => ({
      [ButtonState.VERIFYING_ACCOUNT]: undefined,
      [ButtonState.PROVIDE_ID]: () => onfido.start(),
      [ButtonState.RETRY_VERIFICATION]: () => onfido.start(),
      [ButtonState.GET_UPDATE]: () => navigation.navigate('Account'),
      [ButtonState.CONTACT_SUPPORT]: () => navigation.navigate('Account'),
      [ButtonState.VERIFY_ACCOUNT]: () =>
        handleVerifyAccount(navigation, account),
      [ButtonState.VERIFY_IDENTITY]: () =>
        handleVerifyIdentity(navigation, account),
    }),
    [account, onfido, navigation],
  );
  const { status } = account || {};

  return (
    <Box>
      <Box height={top} />
      <Box
        padding="space-400"
        flexDirection={{
          mobile: 'column',
          tablet: 'column',
          desktop: 'row',
        }}
        gap={{
          mobile: 'space-300',
          tablet: 'space-300',
          desktop: 'space-600',
        }}
        paddingTop={!top ? 'space-400' : 'space-0'}
      >
        <BonanzaLogo />
        <BonusInfo
          flexGrow={1}
          promos={promos}
          textKey="yourWelcomeBonus"
          textNS="onboarding"
          progress={status === 'Approved' ? 98 : 95}
          gap={{
            mobile: promos.length === 1 ? 'space-400' : undefined,
            tablet: 'space-400',
            desktop: 'space-400',
          }}
          imageSize={64}
          horizontal
        />
        {buttonState ? (
          <Button
            locKey={`${buttonState}Button`}
            loading={onfido.initializing}
            disabled={buttonState === ButtonState.VERIFYING_ACCOUNT}
            alignSelf={{
              mobile: undefined,
              tablet: undefined,
              desktop: 'center',
            }}
            onPress={actions[buttonState]}
            locNS="onboarding"
          />
        ) : (
          <Skeleton
            height={40}
            alignSelf={{
              mobile: undefined,
              tablet: undefined,
              desktop: 'center',
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default memo(OnboardingHeader);
